import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogRoute, TransitioningDialogOverlay, useOverlay } from '@fs/zion-ui'
import AddExistingPersonPanel from './AddExistingPersonPanel'
import PreviousRelationshipsStep, { PreviousRelationshipsFooter } from './components/PreviousRelationshipsStep'
import ChoosePrimaryPersonStep, { ChoosePrimaryPersonFooter } from './components/ChoosePrimaryPersonStep'
import { useAddExistingPersonFlow } from './useAddExistingPersonFlow'
import { ADD_EXISTING_PERSON_STEPS, ADD_EXISTING_PERSON_STEP_PATHS } from './utils/addExistingPersonConstants'

/**
 * Renders the Add Existing Person flow: the selection InfoSheet, then whichever of the two confirmation
 * steps apply.
 *
 * The confirmations share one dialog and the patron moves between them by routing, which is what
 * gives the flow its transitions and its header Back arrow.
 *
 * @param {Object} params
 * @param {Object} params.overlay - `useOverlay()` object controlling the selection InfoSheet
 * @param {string} params.entryPoint - One of
 *   {@link ./utils/addExistingPersonConstants}.ADD_EXISTING_PERSON_ENTRY_POINTS
 * @param {string} [params.focusPersonName] - Focus person's name; omitted when there is no focus person
 * @param {string} [params.focusPersonId] - The focus person, who is never a selectable row
 * @param {Array<string>} [params.pendingMemberIds] - The subset of `groupMemberIds` staged but not yet saved
 * @param {Array<string>} [params.groupMemberIds] - Person ids already in the group, from the caller's
 *   live form state rather than SLS, so a name added but not yet saved cannot be added twice
 * @param {(result: import('./useAddExistingPersonFlow').AddExistingPersonResult) => (void|Promise<void>)}
 *   [params.onSave] Called with everything the patron agreed to. A returned promise is awaited
 *   before either surface comes down, so a save can hold them open until it settles.
 * @returns {React.ReactElement}
 */
const AddExistingPersonFlow = ({
  overlay,
  entryPoint,
  focusPersonName,
  focusPersonId,
  groupMemberIds = [],
  pendingMemberIds = [],
  onSave = () => {},
}) => {
  const [t] = useTranslation()
  // Dismissing a confirmation returns the patron to the control that raised it.
  const confirmButtonRef = useRef(null)

  const handleComplete = useCallback((result) => onSave(result), [onSave])

  const {
    dialogSteps,
    movingPersons,
    atRiskGroups,
    primaryPersonIds,
    selectPersons,
    togglePrimaryPerson,
    completeFlow,
    reset,
  } = useAddExistingPersonFlow({
    entryPoint,
    pendingMemberIds,
    onComplete: handleComplete,
  })

  const dialogOverlay = useOverlay({ focusBackRef: confirmButtonRef })
  const [saving, setSaving] = useState(false)

  /**
   * Waits for the save, then takes the surfaces down inner-to-outer.
   *
   * `onSave` resolves only once SLS has answered and the new group is in state, so holding here
   * means the list behind is already correct when the sheet goes.
   *
   * A rejection is swallowed rather than rethrown: no caller awaits this, so letting it through
   * would only surface as an unhandled rejection. Reporting the failure is `onSave`'s job.
   *
   * @param {Promise<void>|undefined} pendingSave - Whatever the caller's `onSave` returned.
   * @returns {Promise<void>}
   */
  const settleSave = useCallback(
    async (pendingSave) => {
      setSaving(true)
      try {
        await pendingSave
      } catch {
        // Intentionally ignored; see above.
      } finally {
        setSaving(false)
        // The dialog's focus-back ref lives inside the sheet, so the sheet has to outlast it.
        dialogOverlay.close()
        overlay.close()
      }
    },
    [dialogOverlay, overlay]
  )

  // Open from the click, never from an effect watching `dialogSteps`: forcing it open from state
  // lets a dismissal be overruled and traps the patron. FSS-15171.
  const handleSelectionConfirmed = useCallback(
    async (personIds) => {
      // The dialog is held through its close animation and its router is built once, so reopening
      // mid-flight would land the patron on the step they just left. Keyed off `transitioning`
      // rather than a delay, because what has to finish is the animation, not a fixed interval.
      if (dialogOverlay.transitioning) return

      const { staged, saving: pendingSave } = selectPersons(personIds)
      if (staged) {
        dialogOverlay.open()
        return
      }
      // Nothing was asked, so the save is already away.
      await settleSave(pendingSave)
    },
    [selectPersons, dialogOverlay, settleSave]
  )

  const handleStepConfirmed = useCallback(() => settleSave(completeFlow()), [completeFlow, settleSave])

  // `InfoSheetPanel` discards a `nonClosable` prop — it takes that flag from the parent sheet's
  // context — and its header Back arrow is wired straight to this `close`. Neutering `close` is the
  // only lever that keeps the sheet standing on an unfinished write. `settleSave` still holds the
  // real one. The parent sheet closing mid-save would leave the panel up, which beats losing it.
  const panelOverlay = useMemo(() => (saving ? { ...overlay, close: () => {} } : overlay), [saving, overlay])

  const [firstStep, secondStep] = dialogSteps

  return (
    <>
      <AddExistingPersonPanel
        overlay={panelOverlay}
        focusPersonName={focusPersonName}
        focusPersonId={focusPersonId}
        groupMemberIds={groupMemberIds}
        confirmButtonRef={confirmButtonRef}
        onConfirm={handleSelectionConfirmed}
        onCancel={reset}
        saving={saving}
      />
      {/* Held through the close transition so the dialog animates out; it still unmounts once
          `transitioning` clears, which is what discards the step router between runs. `reset` runs
          on that unmount rather than at the start of the close, so the steps below outlast the
          animation. The `firstStep` guard covers the one case that does empty them early — a fresh
          selection needing no confirmation, chosen mid-transition — where `initialLocation` would
          go undefined and Zion throws. */}
      {(dialogOverlay.isOpen || dialogOverlay.transitioning) && firstStep && (
        <TransitioningDialogOverlay
          {...dialogOverlay}
          initialLocation={ADD_EXISTING_PERSON_STEP_PATHS[firstStep]}
          onClose={reset}
          nonClosable={saving}
          size="sm"
          data-testid="add-existing-person-dialog"
        >
          {dialogSteps.includes(ADD_EXISTING_PERSON_STEPS.PREVIOUS_RELATIONSHIPS) && (
            <DialogRoute
              path={ADD_EXISTING_PERSON_STEP_PATHS[ADD_EXISTING_PERSON_STEPS.PREVIOUS_RELATIONSHIPS]}
              title={t('add-existing-person.previous-relationships.title', 'Previous Relationships')}
              busy={saving}
              footer={
                <PreviousRelationshipsFooter
                  nextPath={ADD_EXISTING_PERSON_STEP_PATHS[secondStep]}
                  saving={saving}
                  onCancel={dialogOverlay.close}
                  onContinue={handleStepConfirmed}
                />
              }
            >
              <PreviousRelationshipsStep
                atRiskGroups={atRiskGroups}
                entryPoint={entryPoint}
                focusPersonName={focusPersonName}
              />
            </DialogRoute>
          )}
          {dialogSteps.includes(ADD_EXISTING_PERSON_STEPS.PRIMARY_PERSON) && (
            <DialogRoute
              path={ADD_EXISTING_PERSON_STEP_PATHS[ADD_EXISTING_PERSON_STEPS.PRIMARY_PERSON]}
              title={t('add-existing-person.primary-person.title', 'Choose a primary person')}
              busy={saving}
              footer={
                <ChoosePrimaryPersonFooter
                  disabled={primaryPersonIds.length === 0}
                  saving={saving}
                  onCancel={dialogOverlay.close}
                  onConfirm={handleStepConfirmed}
                />
              }
            >
              <ChoosePrimaryPersonStep
                movingPersons={movingPersons}
                primaryPersonIds={primaryPersonIds}
                onToggle={togglePrimaryPerson}
              />
            </DialogRoute>
          )}
        </TransitioningDialogOverlay>
      )}
    </>
  )
}

export default AddExistingPersonFlow
